import type { SelectProps } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState, type JSX } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from '@mui/material';

export type NursingHomeDropdownProps = SelectProps & {
  disabled?: boolean;
  nursingHomes: Array<{
    value: string;
    label: string;
  }>;
  nursingHomeId?: string;
  onChangeNursingHome: (value: string) => void;
  errorMessage?: string;
  name?: string;
};

export const NursingHomeDropdown = ({
  nursingHomeId,
  onChangeNursingHome,
  nursingHomes,
  disabled,
  errorMessage,
  name,
}: NursingHomeDropdownProps): JSX.Element => {
  const { t } = useTranslation();

  const singleNursingHomeAvailable = useMemo(() => {
    return nursingHomes.length === 1;
  }, [nursingHomes.length]);

  const [selectedNursingHome, setSelectedNursingHome] = useState<string | null>(
    () => (singleNursingHomeAvailable ? nursingHomes[0].value : null)
  );

  useEffect(() => {
    if (nursingHomes.length > 0) {
      if (singleNursingHomeAvailable) {
        setSelectedNursingHome(nursingHomes[0].value);
        return;
      }
      const newSelectedNursingHome =
        nursingHomes.find(
          (nursingHome) => nursingHome.value === nursingHomeId
        ) ?? null;
      if (newSelectedNursingHome?.value !== selectedNursingHome) {
        setSelectedNursingHome(newSelectedNursingHome?.value ?? null);
      }
    }
    // selectedNursingHome missing to avoid circular dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nursingHomeId, nursingHomes]);

  const selected = useMemo(() => {
    if (!selectedNursingHome) {
      return null;
    }

    const selectedLabel = nursingHomes.find(
      (nursingHome) => nursingHome.value === selectedNursingHome
    )?.label;

    return {
      value: selectedNursingHome,
      label: selectedLabel ?? '',
    };
  }, [nursingHomes, selectedNursingHome]);

  const defaultInputValue = selected?.label ?? '';
  const [inputValue, setInputValue] = useState(defaultInputValue);
  useEffect(() => {
    setInputValue(defaultInputValue);
  }, [setInputValue, defaultInputValue]);

  const value = useMemo(() => {
    return (
      selected ?? {
        value: '',
        label: '',
      }
    );
  }, [selected]);

  const [open, setOpen] = useState(false);

  return (
    <Autocomplete
      disabled={singleNursingHomeAvailable || disabled}
      open={open}
      value={value}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
      onChange={(_, value) => {
        setOpen(false);
        onChangeNursingHome(value.value);
      }}
      slotProps={{
        listbox: {
          style: {
            maxHeight: '354px',
          },
        },
      }}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onInputChange={(_, value) => {
        setInputValue(value);
      }}
      disableClearable={true}
      renderInput={({ ...params }) => (
        <TextField
          {...(name ? { name: name } : {})}
          label={t('sp-web-app.nursing-home')}
          type={'text'}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          {...params}
        />
      )}
      options={nursingHomes ?? []}
    />
  );
};
